import { useEffect, useRef } from "react";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { useMagicMomentStore } from "./use-magic-moment-store";
import { BannerHeader } from "./banner-header";
import { BannerActionButton } from "./banner-action-button";
import { MagicMomentDrawer } from "./magic-moment-drawer";
import { TipsDrawer } from "./tips-drawer";
import { Camera, Info, RefreshCw, Sparkles } from "lucide-react";

interface Day2BannerProps {
  teenGender: "male" | "female" | null;
  teenId?: string | null;
  conversationId?: string | null;
  onShareMoment: () => void;
  onStartSwap: () => void;
  onInsertToChat?: (text: string) => void;
}

export function Day2Banner({
  teenGender,
  teenId,
  conversationId,
  onShareMoment,
  onStartSwap,
  onInsertToChat,
}: Day2BannerProps) {
  const { track } = useTrackEvent();
  const viewTracked = useRef(false);
  const bannerExpanded = useMagicMomentStore((s) => s.bannerExpanded);
  const currentView = useMagicMomentStore((s) => s.currentView);
  const toggleBanner = useMagicMomentStore((s) => s.toggleBanner);
  const navigateToView = useMagicMomentStore((s) => s.navigateToView);
  const collapse = useMagicMomentStore((s) => s.collapse);

  useEffect(() => {
    if (!viewTracked.current) {
      viewTracked.current = true;
      track(PostHogEvents.DAY2_BANNER_VIEW, {
        teen_id: teenId,
        conversation_id: conversationId,
      });
    }
  }, []);

  const handleToggle = () => {
    const newState = bannerExpanded ? "collapsed" : "expanded";
    track(PostHogEvents.DAY2_BANNER_TOGGLE, {
      teen_id: teenId,
      state: newState,
    });
    toggleBanner();
  };

  const handleAction = (action: string, callback: () => void) => {
    track(PostHogEvents.DAY2_ACTION_CLICK, { teen_id: teenId, action });
    callback();
  };

  if (currentView === "magic-moment") {
    return (
      <MagicMomentDrawer
        teenGender={teenGender}
        teenId={teenId}
        onInsertToChat={onInsertToChat}
      />
    );
  }

  if (currentView === "tips") {
    return <TipsDrawer teenId={teenId} />;
  }

  return (
    <div className="rounded-t-[16px] border-x border-t border-[#ece8e1] bg-[#f7f5f1] px-[17px] pb-4 pt-[17px]">
      <BannerHeader
        title="Day 2"
        expanded={bannerExpanded}
        onToggle={handleToggle}
      />
      {bannerExpanded && (
        <div className="mt-1 flex flex-col gap-2">
          <p className="text-[14px] leading-[20px] text-[#58534c]">
            This is an important moment to slow down and be thoughtful. The
            tools below are here if you need them.
          </p>
          <div className="flex flex-wrap gap-x-1">
            <BannerActionButton
              icon={<Sparkles className="h-3 w-3" />}
              label="Magic moment"
              onClick={() =>
                handleAction("magic_moment", () =>
                  navigateToView("magic-moment"),
                )
              }
            />
            <BannerActionButton
              icon={<RefreshCw className="h-3 w-3" />}
              label="Start a Swap"
              onClick={() =>
                handleAction("start_swap", () => {
                  collapse();
                  onStartSwap();
                })
              }
            />
            <BannerActionButton
              icon={<Camera className="h-3 w-3" />}
              label="Share a moment"
              onClick={() =>
                handleAction("share_moment", () => {
                  collapse();
                  onShareMoment();
                })
              }
            />
            <BannerActionButton
              icon={<Info className="h-3 w-3" />}
              label="Tips"
              onClick={() =>
                handleAction("tips", () => navigateToView("tips"))
              }
            />
          </div>
        </div>
      )}
    </div>
  );
}
